import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronLeft, ChevronRight, History, CheckCircle, XCircle, Clock, SkipForward, RefreshCw } from 'lucide-react'
import { automationApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDate } from '@/lib/utils'
import type { AutomationLog, AutomationLogStatus, TriggerType } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = '__all__'

const STATUS_CONFIG: Record<AutomationLogStatus, { icon: React.ReactNode; color: string; label: string }> = {
  PENDING: { icon: <Clock className="h-4 w-4" />, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  SUCCESS: { icon: <CheckCircle className="h-4 w-4" />, color: 'bg-green-100 text-green-800', label: 'Success' },
  FAILED: { icon: <XCircle className="h-4 w-4" />, color: 'bg-red-100 text-red-800', label: 'Failed' },
  SKIPPED: { icon: <SkipForward className="h-4 w-4" />, color: 'bg-gray-100 text-gray-800', label: 'Skipped' },
}

const TRIGGER_LABELS: Record<TriggerType, string> = {
  LEAD_CREATED: 'Lead Created',
  LEAD_STATUS_CHANGED: 'Lead Status Changed',
  LEAD_ASSIGNED: 'Lead Assigned',
  LEAD_CONVERTED: 'Lead Converted',
  DEAL_CREATED: 'Deal Created',
  DEAL_STAGE_CHANGED: 'Deal Stage Changed',
  DEAL_WON: 'Deal Won',
  DEAL_LOST: 'Deal Lost',
  DEAL_ASSIGNED: 'Deal Assigned',
  ACTIVITY_CREATED: 'Activity Created',
  TASK_COMPLETED: 'Task Completed',
  TASK_OVERDUE: 'Task Overdue',
  REMINDER_DUE: 'Reminder Due',
}

export default function AutomationLogsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES)

  const { data: logsResponse, isLoading, refetch } = useQuery({
    queryKey: ['automation-logs', currentPage, pageSize, statusFilter],
    queryFn: () => automationApi.getAutomationLogs({
      page: currentPage,
      page_size: pageSize,
      status: statusFilter !== ALL_STATUSES ? statusFilter as AutomationLogStatus : undefined,
    }),
  })

  const logs = logsResponse?.logs || []
  const totalPages = logsResponse?.total_pages || 1
  const total = logsResponse?.total || 0

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setCurrentPage(1)
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/automations')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <History className="h-8 w-8 text-muted-foreground" />
              {t('crm.automations.logsTitle', 'Automation Logs')}
            </h1>
            <p className="text-muted-foreground">
              {t('crm.automations.logsDescription', 'View execution history of your automation rules')}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t('crm.automations.filterByStatus', 'Filter by status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STATUSES}>{t('common.allStatuses', 'All Statuses')}</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        {config.icon}
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground">
              {t('crm.automations.totalLogs', '{{count}} log entries', { count: total })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <div className="h-[calc(100vh-350px)] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>{t('common.status', 'Status')}</TableHead>
                  <TableHead>{t('crm.automations.trigger', 'Trigger')}</TableHead>
                  <TableHead>{t('crm.automations.entityType', 'Entity Type')}</TableHead>
                  <TableHead>{t('crm.automations.entityId', 'Entity ID')}</TableHead>
                  <TableHead>{t('crm.automations.actionsExecuted', 'Actions')}</TableHead>
                  <TableHead>{t('crm.automations.executedAt', 'Executed At')}</TableHead>
                  <TableHead>{t('crm.automations.error', 'Error')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      {t('common.loading', 'Loading...')}
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <History className="h-12 w-12 text-muted-foreground/50" />
                        <p className="text-muted-foreground">
                          {t('crm.automations.noLogs', 'No automation logs found')}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log: AutomationLog) => {
                    const statusConfig = STATUS_CONFIG[log.status]
                    return (
                      <TableRow key={log.log_id}>
                        <TableCell>
                          <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit`}>
                            {statusConfig.icon}
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {TRIGGER_LABELS[log.trigger_type] || log.trigger_type}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.entity_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {log.entity_id.slice(0, 8)}...
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {log.actions_executed?.length || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(log.executed_at)}
                        </TableCell>
                        <TableCell>
                          {log.error_message && (
                            <span className="text-sm text-destructive truncate max-w-xs block" title={log.error_message}>
                              {log.error_message}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('common.rowsPerPage', 'Rows per page')}:</span>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            {t('common.pageOf', 'Page {{current}} of {{total}}', { current: currentPage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

